import { Icon, ListItem, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  FileFolderSortOption,
  FileFolderSortOptions,
  FileFolderViewMode,
  FileFolderViewModes,
} from 'file_folder_repository/types'
import React, { useState } from 'react'
import { View } from 'react-native'
import { Collapsible } from 'shared/components/list/collapsible'
import { PressableOpacity } from 'shared/components/pressable_opacity'

export function OptionsView({
  onPressSortOption,
  onPressViewMode,
  selectedSortOption,
  selectedViewMode,
}: {
  onPressSortOption: (option: FileFolderSortOption) => void
  onPressViewMode: (option: FileFolderViewMode) => void
  selectedSortOption: FileFolderSortOption
  selectedViewMode: FileFolderViewMode
}) {
  return (
    <>
      <ViewModesList
        onPress={onPressViewMode}
        selectedMode={selectedViewMode}
      />
      {selectedViewMode !== 'timeline' ? (
        <SortOrderListView
          onPress={onPressSortOption}
          selectedOption={selectedSortOption}
        />
      ) : null}
    </>
  )
}

interface SortOrderListViewProps {
  onPress(option: FileFolderSortOption): void
  selectedOption: FileFolderSortOption
}

function SortOrderListView({
  onPress,
  selectedOption,
}: SortOrderListViewProps) {
  const styles = useStyles()
  const [expanded, setExpanded] = useState(false)

  return (
    <Collapsible
      expanded={expanded}
      onPress={() => setExpanded(!expanded)}
      title={t('sortBy')}
      value={t(selectedOption)}
      containerStyle={styles.collapsibleContainer}
    >
      <View style={styles.detailsContainer}>
        {FileFolderSortOptions.map(option => (
          <SortOptionRow
            key={option}
            option={option}
            onPress={() => onPress(option)}
            selected={option === selectedOption}
          />
        ))}
      </View>
    </Collapsible>
  )
}

function SortOptionRow({
  onPress,
  option,
  selected,
}: {
  option: FileFolderSortOption
  onPress: () => void
  selected: boolean
}) {
  const styles = useStyles()
  return (
    <PressableOpacity onPress={onPress}>
      <ListItem containerStyle={styles.sortOptionContainer}>
        <ListItem.Content style={styles.sortOptionTitleContainer}>
          <ListItem.Title>{t(option)}</ListItem.Title>
        </ListItem.Content>
        <ListItem.CheckBox
          onPress={onPress}
          iconRight
          checkedIcon="dot-circle-o"
          uncheckedIcon="circle-o"
          checked={selected}
          containerStyle={styles.sortOptionCheckbox}
        />
      </ListItem>
    </PressableOpacity>
  )
}

interface ViewModeListProps {
  onPress(mode: FileFolderViewMode): void
  selectedMode: FileFolderViewMode
}

function ViewModesList({ onPress, selectedMode }: ViewModeListProps) {
  const styles = useStyles()
  const [expanded, setExpanded] = useState(true)

  return (
    <Collapsible
      expanded={expanded}
      onPress={() => setExpanded(!expanded)}
      title={t('viewAs')}
      value={t(selectedMode)}
      containerStyle={styles.collapsibleContainer}
    >
      <View style={styles.viewModeItemsContainer}>
        {FileFolderViewModes.map(option => (
          <ViewModeItem
            key={option}
            option={option}
            onPress={() => {
              onPress(option)
            }}
            selected={option === selectedMode}
          />
        ))}
      </View>
    </Collapsible>
  )
}

function ViewModeItem({
  onPress,
  option,
  selected,
}: {
  option: FileFolderViewMode
  onPress: () => void
  selected: boolean
}) {
  const styles = useStyles()
  return (
    <PressableOpacity onPress={onPress}>
      <View style={[styles.viewModeItem]}>
        <ViewModeItemIcon mode={option} />
        <Text style={{ marginBottom: 5 }}>{t(option)}</Text>
        <ListItem.CheckBox
          onPress={onPress}
          iconRight
          checkedIcon="dot-circle-o"
          uncheckedIcon="circle-o"
          checked={selected}
          containerStyle={styles.sortOptionCheckbox}
        />
      </View>
    </PressableOpacity>
  )
}

function ViewModeItemIcon({ mode }: { mode: FileFolderViewMode }) {
  const { theme } = useTheme()
  const { name, type } =
    mode === 'grid'
      ? { name: 'grid', type: 'entypo' }
      : mode === 'list'
      ? { name: 'list', type: 'entypo' }
      : { name: 'timeline-clock-outline', type: 'material-design' }
  return (
    <Icon
      name={name}
      type={type}
      size={40}
      color={theme.colors.black}
      style={{ paddingBottom: 5 }}
    />
  )
}

const useStyles = makeStyles(theme => ({
  detailsContainer: {
    paddingBottom: 10,
    flex: 1,
  },
  bottomSheetBackground: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  bottomSheetHandleIndicator: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.black : theme.colors.grey4,
  },
  headerText: {
    marginLeft: 15,
    fontWeight: 'bold',
    color: 'grey',
    marginBottom: 5,
  },
  sortOptionContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  sortOptionTitleContainer: { margin: 0, padding: 0 },
  sortOptionCheckbox: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  collapsibleContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  viewModeItemsContainer: {
    flex: 1,
    paddingBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
  viewModeItem: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
}))
